import { AfterViewInit, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { EMPTY, merge } from 'rxjs';
import { debounceTime, filter, map, startWith, switchMap } from 'rxjs/operators';
import { TabFilterId as ThingieFilterId } from 'src/app/components/list-thingies/list-thingies.component';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { AuthService } from 'src/app/services/auth/auth.service';
import { TemplateService } from 'src/app/services/templates/template.service';
import { UserDataService } from 'src/app/services/user-data/user-data.service';
import {
  DASHBOARD_PAGE_CARDS_SIZE_STORAGE_KEY,
  DASHBOARD_PAGE_GROUP_BY_STORAGE_KEY,
  DASHBOARD_PAGE_THINGIES_SIZE_STORAGE_KEY,
  DASHBOARD_PAGE_VIEW_MODE_STORAGE_KEY,
  DASHBOARD_THINGIE_TAB_STORAGE_KEY,
  DASHBOARD_THINGIES_OR_DEVICES_PAGE_STORAGE_KEY,
  getDashboardSetting,
  updateDashboardSetting
} from 'src/app/utility/dashboard-helper';
import { DefaultUrls } from 'src/assets/default.urls';
import { SubSink } from 'subsink';
import { ListThingiesComponent } from '../../components/list-thingies/list-thingies.component';
import { MenuBarComponent } from '../../components/menu-bar/menu-bar.component';

export interface IProjectInfo {
  projectName: string;
  projectId: string;
  projectNotEmpty: boolean;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  imports: [MenuBarComponent, MaterialImportsModule, ListThingiesComponent]
})
export class DashboardComponent implements OnInit, OnDestroy, AfterViewInit {
  protected selectedThingieTab: ThingieFilterId = 'all';

  private _groupByExperimentChecked = true;
  public get groupByExperimentChecked() {
    return this._groupByExperimentChecked;
  }

  public set groupByExperimentChecked(checked: boolean) {
    this._groupByExperimentChecked = checked;
  }

  protected mode = '';
  private _viewMode: 'thingies' | 'cards'  = 'cards';

  private subscriptions = new SubSink();

  protected pageSize = 16;
  protected currentPage = 1;
  protected refreshListSignal = false;
  private currentUserID!: string;
  protected isLoading = signal(false);

  public get viewMode(): 'thingies' | 'cards' {
    return this._viewMode;
  }

  public set viewMode(newViewMode: 'thingies' | 'cards') {
    this._viewMode = newViewMode;
  }


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    private auth: AuthService,
    private userDataService: UserDataService,
    private templateService: TemplateService
  ) {
    this.subscriptions.sink = router.events.subscribe((event) => {
      if (event instanceof NavigationStart && router.currentNavigation()?.extras?.state?.refreshDashboard === true) {
        this.refreshListSignal = !this.refreshListSignal;
      }
    });
  }

  public async ngOnInit() {
    this.isLoading.set(true);
    this.currentUserID = await this.auth.getCurrentUserId();
    // check for the list or cards view
    const routeState = this.processRouteState(this.route.snapshot);
    this.mode = routeState.pageMode;
    this._viewMode = routeState.viewMode;
    this.pageSize = parseInt(routeState.pageSize, 10);
    this.selectedThingieTab = routeState.thingieTab;
    this.currentPage = parseInt(routeState.page, 10);

    // when navigating to this page, update the title
    // need to listen to router events because no route observables are
    // triggered due to the route reuse strategy.
    this.subscriptions.sink = this.router.events
      .pipe(filter(evt => evt instanceof NavigationEnd))
      .subscribe(() => {
        if (this.route.component === DashboardComponent) {
          this.setTitle();
        }
      });

    this.subscriptions.sink = merge(this.route.params, this.route.queryParams)
      .pipe(
        map(() => [this.route.snapshot.params, this.route.snapshot.queryParams]),
        startWith([this.route.snapshot.params, this.route.snapshot.queryParams]),
        debounceTime(10),
        switchMap(([pathParams, queryParams]) => {
          if (queryParams.tab !== undefined) {
            this.selectedThingieTab = queryParams.tab as ThingieFilterId;
          } else {
            this.selectedThingieTab = 'all';
          }

          if (this.router.url.startsWith(DefaultUrls.route.dashboard)) {
            const routeState = this.processRouteState(this.route.snapshot);
            const mode = pathParams.id;
            if (mode !== null && mode !== '') {
              this.mode = routeState.pageMode;
              this._viewMode = routeState.viewMode;
              this._groupByExperimentChecked = routeState.isGroupingEnabled;
              this.currentPage = parseInt(routeState.page, 10);
              this.pageSize = parseInt(routeState.pageSize, 10);
              this.selectedThingieTab = routeState.thingieTab;
            } else {
              void this.router.navigate([DefaultUrls.route.dashboardMode(routeState.modeUrl)], { queryParams });
            }
          }

          return EMPTY;
        })
      )
      .subscribe();

    this.isLoading.set(false);
  }

  private processRouteState(snapshot: ActivatedRouteSnapshot) {
    const savedPageMode: string | null = getDashboardSetting(this.currentUserID, DASHBOARD_THINGIES_OR_DEVICES_PAGE_STORAGE_KEY);
    const savedViewMode: string | null = getDashboardSetting(this.currentUserID, DASHBOARD_PAGE_VIEW_MODE_STORAGE_KEY);
    const savedIsGroupingEnabled: string | null = getDashboardSetting(this.currentUserID, DASHBOARD_PAGE_GROUP_BY_STORAGE_KEY);
    const savedPageSizeCards = getDashboardSetting(this.currentUserID, DASHBOARD_PAGE_CARDS_SIZE_STORAGE_KEY);
    const savedPageSizeThingieList = getDashboardSetting(this.currentUserID, DASHBOARD_PAGE_THINGIES_SIZE_STORAGE_KEY);
    const savedThingieTab = getDashboardSetting(this.currentUserID, DASHBOARD_THINGIE_TAB_STORAGE_KEY);
    const pageMode = 'thingies';
    let viewMode = savedViewMode || 'cards';
    let isGroupingEnabled = savedIsGroupingEnabled || 'true';
    const pageSizeCards = savedPageSizeCards || '8';
    const pageSizeThingieList = savedPageSizeThingieList || '10';
    let thingieTab = savedThingieTab || 'all';
    const pageModeFromUrl = snapshot.paramMap.get('id');
    const tabFromUrl = snapshot.queryParamMap.get('tab');
    const groupingFromUrl = snapshot.queryParamMap.get('grouped');
    let page = snapshot.queryParams.page;
    page = (page && page > 0) ? page : 1;

    let pageSize = '';

    if (pageModeFromUrl !== null && pageModeFromUrl === 'thingies') {
      viewMode = 'thingies';
    } else {
      viewMode = 'cards';
    }
    if (tabFromUrl !== null) {
      thingieTab = tabFromUrl;
    }
    if (viewMode === 'thingies') {
      pageSize = pageSizeThingieList;
    } else {
      pageSize = pageSizeCards;
    }
    if (groupingFromUrl !== null) {
      isGroupingEnabled = groupingFromUrl;
    }
    if (pageMode !== savedPageMode) {
      updateDashboardSetting(this.currentUserID, DASHBOARD_THINGIES_OR_DEVICES_PAGE_STORAGE_KEY, pageMode);
    }
    if (viewMode !== savedViewMode) {
      updateDashboardSetting(this.currentUserID, DASHBOARD_PAGE_VIEW_MODE_STORAGE_KEY, viewMode);
    }
    if (isGroupingEnabled !== savedIsGroupingEnabled) {
      updateDashboardSetting(this.currentUserID, DASHBOARD_PAGE_GROUP_BY_STORAGE_KEY, isGroupingEnabled);
    }
    if (thingieTab !== savedThingieTab) {
      updateDashboardSetting(this.currentUserID, DASHBOARD_THINGIE_TAB_STORAGE_KEY, thingieTab);
    }


    const routeState = {
      pageMode, // returns only 'thingies',
      viewMode: viewMode as 'thingies' | 'cards',
      modeUrl: viewMode,
      isGroupingEnabled: isGroupingEnabled === 'true',
      page,
      pageSize,
      thingieTab: thingieTab as ThingieFilterId
    };

    return routeState;
  }

  public ngAfterViewInit(): void {
    this.setTitle();
  }

  /**
   * Sets titles for window and the menu bar
   */
  public setTitle() {
    this.titleService.setTitle('Dashboard');
  }

  public ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}

